import React, { useState, useEffect } from 'react';

import ProductModal from '../components/ProductModal';
import Alert from '../components/Alert';
import api from '../utils/api';
import {
  Package,
  Plus,
  Search,
  Loader2,
  Pencil,
  Trash2,
  BarChart3,
  X
} from 'lucide-react';
import './Products.css';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  useEffect(() => { fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/products?search=${search}`);
      setProducts(response.data.products);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => { setEditingProduct(null); setShowModal(true); };
  const handleEdit = (product) => { setEditingProduct(product); setShowModal(true); };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await api.delete(`/products/${id}`);
        setSuccess('Product deleted successfully');
        fetchProducts();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete product');
      }
    }
  };

  const handleSubmit = async (productData) => {
    try {
      if (editingProduct) {
        await api.put(`/products/${editingProduct._id}`, productData);
        setSuccess('Product updated successfully');
      } else {
        await api.post('/products', productData);
        setSuccess('Product added successfully');
      }
      setShowModal(false);
      fetchProducts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save product');
    }
  };

  return (
    <div className="products-page">
      {/* Page Header */}
      <div className="products-header">
        <div className="products-header-left">
          <div className="products-header-icon">
            <Package size={22} color="#0793A7" />
          </div>
          <div>
            <h1 className="products-title">Products & Services</h1>
            <p className="products-subtitle">Manage your product catalog and pricing</p>
          </div>
        </div>
        <button className="btn btn-primary" onClick={handleAdd}>
          <Plus size={16} />
          Add New Product
        </button>
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

      {/* Search Bar */}
      <div className="products-search-wrap">
        <Search size={16} className="products-search-icon" />
        <input
          type="text"
          className="products-search-input"
          placeholder="Search by name, product ID, description, or HSN code…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>

      {/* Content */}
      {loading ? (
        <div className="products-loading">
          <Loader2 size={36} className="products-spinner" />
          <span>Loading products…</span>
        </div>
      ) : products.length === 0 ? (
        <div className="products-empty">
          <Package size={56} className="products-empty-icon" />
          <h3>No Products Found</h3>
          <p>{search ? 'Try adjusting your search terms' : 'Add your first product to get started'}</p>
          {!search && (
            <button className="btn btn-primary" onClick={handleAdd}>
              <Plus size={16} /> Add First Product
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="products-grid">
            {products.map(product => (
              <div className="product-card" key={product._id}>
                {/* Top accent bar */}
                <div className="product-card-accent" />

                <div className="product-card-badges">
                  <span className="product-id-badge">#{product.productId}</span>
                  <span className={`product-gst-badge ${product.gstRate === 0 ? 'gst-zero' : ''}`}>
                    {product.gstRate}% GST
                  </span>
                </div>

                <h3 className="product-name">{product.name}</h3>

                {product.description && (
                  <p className="product-description">{product.description}</p>
                )}

                <div className="product-details-row">
                  <div className="product-detail-item">
                    <div className="product-detail-label">HSN/SAC</div>
                    <code className="product-hsn">{product.hsnSac}</code>
                  </div>
                  <div className="product-detail-item">
                    <div className="product-detail-label">Unit</div>
                    <span className="product-unit-badge">{product.unit}</span>
                  </div>
                  {product.type !== 'service' ? (
                    <div className="product-detail-item">
                      <div className="product-detail-label">Stock</div>
                      <span className={`product-stock-badge ${(product.stock || 0) <= 10 ? 'stock-low' : ''}`}>
                        {product.stock || 0}
                      </span>
                    </div>
                  ) : (
                    <div className="product-detail-item">
                      <div className="product-detail-label">Type</div>
                      <span className="product-stock-badge" style={{ background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd' }}>
                        Service
                      </span>
                    </div>
                  )}
                </div>

                <div className="product-price-row">
                  <div>
                    <div className="product-rate-label">Rate</div>
                    <div className="product-rate-value">₹{product.rate.toFixed(2)}</div>
                  </div>
                  <div className="product-actions">
                    <button className="product-edit-btn" onClick={() => handleEdit(product)}>
                      <Pencil size={13} /> Edit
                    </button>
                    <button className="product-delete-btn" onClick={() => handleDelete(product._id)}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="products-footer">
            <div className="products-footer-left">
              <BarChart3 size={16} color="#0793A7" />
              <span><strong>{products.length}</strong> product{products.length !== 1 ? 's' : ''} in catalog</span>
            </div>
            {search && (
              <button className="products-clear-btn" onClick={() => { setSearch(''); setSearchInput(''); }}>
                <X size={13} /> Clear search
              </button>
            )}
          </div>
        </>
      )}

      <ProductModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmit}
        product={editingProduct}
      />
    </div>
  );
};

export default Products;
